<template>
  <div class="chat-page">
    <!-- Sidebar: instrucciones y preguntas frecuentes -->
    <div
      class="sidebar-panel"
      :class="{ 'sidebar-collapsed': sidebarCollapsed }"
    >
      <!-- Toggle button -->
      <button
        class="sidebar-toggle-btn"
        :title="sidebarCollapsed ? 'Expandir panel' : 'Colapsar panel'"
        @click="sidebarCollapsed = !sidebarCollapsed"
      >
        <feather-icon
          :icon="sidebarCollapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'"
          size="16"
        />
      </button>

      <b-card
        v-show="!sidebarCollapsed"
        no-body
        class="sidebar-card"
      >
        <b-card-body>
          <div class="text small">
            <feather-icon
              icon="AlertCircleIcon"
              size="14"
              class="mr-25"
            />
            El asistente solo puede <strong>consultar</strong> datos, no puede modificar ni crear registros.
          </div>

          <hr>

          <p class="text small mb-1">
            Puedes consultar informacion sobre:
          </p>
          <ul class="feature-list">
            <li>
              <feather-icon
                icon="BoxIcon"
                size="14"
                class="mr-50"
              />
              Pedidos y su estado
            </li>
            <li>
              <feather-icon
                icon="FileTextIcon"
                size="14"
                class="mr-50"
              />
              Cotizaciones
            </li>
            <li>
              <feather-icon
                icon="PackageIcon"
                size="14"
                class="mr-50"
              />
              Productos y SKUs
            </li>
            <li>
              <feather-icon
                icon="ArchiveIcon"
                size="14"
                class="mr-50"
              />
              Inventario / Stock
            </li>
            <li>
              <feather-icon
                icon="UsersIcon"
                size="14"
                class="mr-50"
              />
              Clientes
            </li>
          </ul>

          <hr>

          <h6 class="mb-75">
            Preguntas frecuentes
          </h6>
          <div class="faq-list">
            <button
              v-for="(q, i) in frequentQuestions"
              :key="i"
              class="faq-btn"
              :disabled="loading"
              @click="askQuestion(q)"
            >
              <feather-icon
                icon="MessageCircleIcon"
                size="14"
                class="mr-50 flex-shrink-0"
              />
              <span>{{ q }}</span>
            </button>
          </div>
        </b-card-body>
      </b-card>
    </div>

    <!-- Chat principal -->
    <div class="chat-panel">
      <b-card
        no-body
        class="chat-card"
      >
        <!-- Header -->
        <b-card-header class="chat-header">
          <div class="d-flex align-items-center">
            <div class="avatar-icon">
              <feather-icon
                icon="ZapIcon"
                size="18"
              />
            </div>
            <div class="ml-75">
              <h5 class="mb-0">
                Asistente Dutton
              </h5>
              <small class="text-muted">IA de consultas</small>
            </div>
          </div>
          <div
            v-if="messages.length > 0"
            class="d-flex align-items-center"
          >
            <b-button
              variant="flat-secondary"
              size="sm"
              class="mr-50"
              :class="{ 'btn-search-active': showSearch }"
              @click="toggleSearch"
            >
              <feather-icon
                icon="SearchIcon"
                size="14"
              />
            </b-button>
            <b-dropdown
              variant="flat-secondary"
              size="sm"
              no-caret
              right
              class="mr-50"
            >
              <template #button-content>
                <feather-icon
                  icon="DownloadIcon"
                  size="14"
                  class="mr-25"
                />
                Exportar
              </template>
              <b-dropdown-item @click="exportAsText">
                <feather-icon
                  icon="FileTextIcon"
                  size="14"
                  class="mr-50"
                />
                Texto (.txt)
              </b-dropdown-item>
              <b-dropdown-item @click="exportAsPdf">
                <feather-icon
                  icon="FileIcon"
                  size="14"
                  class="mr-50"
                />
                PDF
              </b-dropdown-item>
            </b-dropdown>
            <b-button
              variant="flat-secondary"
              size="sm"
              @click="clearChat"
            >
              <feather-icon
                icon="TrashIcon"
                size="14"
                class="mr-25"
              />
              Limpiar
            </b-button>
          </div>
        </b-card-header>

        <!-- Search bar -->
        <div
          v-if="showSearch"
          class="search-bar"
        >
          <feather-icon
            icon="SearchIcon"
            size="14"
            class="search-icon"
          />
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Buscar en la conversación..."
            @keydown.escape="clearSearch"
          >
          <span
            v-if="searchQuery"
            class="search-results-count"
          >
            {{ searchMatchCount }} resultado{{ searchMatchCount !== 1 ? 's' : '' }}
          </span>
          <button
            class="search-clear-btn"
            @click="clearSearch"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </button>
        </div>

        <!-- Messages area -->
        <div
          ref="chatBody"
          class="chat-body"
          @click="handleEntityClick"
        >
          <!-- Welcome state -->
          <div
            v-if="messages.length === 0"
            class="welcome-state"
          >
            <div class="welcome-icon">
              <feather-icon
                icon="ZapIcon"
                size="40"
              />
            </div>
            <h4>{{ welcomeData ? welcomeData.greeting : 'Hola' }}, soy el Asistente Dutton</h4>
            <div
              v-if="welcomeData && (welcomeData.pending_orders > 0 || welcomeData.pending_quotes > 0)"
              class="welcome-stats mb-1"
            >
              <span
                v-if="welcomeData.pending_orders > 0"
                class="badge badge-light-primary mr-50"
              >
                <feather-icon
                  icon="BoxIcon"
                  size="14"
                  class="mr-25"
                />
                {{ welcomeData.pending_orders }} pedidos pendientes
              </span>
              <span
                v-if="welcomeData.pending_quotes > 0"
                class="badge badge-light-info"
              >
                <feather-icon
                  icon="FileTextIcon"
                  size="14"
                  class="mr-25"
                />
                {{ welcomeData.pending_quotes }} cotizaciones pendientes
              </span>
            </div>
            <p
              v-else
              class="text-muted"
            >
              Preguntame sobre pedidos, cotizaciones, productos, inventario o clientes. Selecciona una pregunta frecuente o escribe tu propia consulta.
            </p>
          </div>

          <div
            v-for="(msg, index) in messages"
            :key="index"
            class="chat-message"
            :class="[
              msg.role === 'user' ? 'chat-message-user' : 'chat-message-assistant',
              searchQuery && !messageMatchesSearch(msg) ? 'msg-dimmed' : ''
            ]"
          >
            <div
              class="msg-avatar"
              :class="msg.role === 'user' ? 'avatar-user' : 'avatar-ai'"
            >
              <feather-icon
                :icon="msg.role === 'user' ? 'UserIcon' : 'ZapIcon'"
                size="16"
              />
            </div>
            <div
              v-if="msg.role === 'assistant'"
              class="message-bubble-wrapper"
              @mouseenter="hoveredMsg = index"
              @mouseleave="hoveredMsg = null"
            >
              <div class="chat-bubble bubble-assistant">
                <button
                  v-if="hoveredMsg === index"
                  class="copy-btn"
                  title="Copiar mensaje"
                  @click="copyMessage(msg.content)"
                >
                  <feather-icon
                    icon="CopyIcon"
                    size="13"
                  />
                </button>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div v-html="formatMessage(msg.content, streaming && index === messages.length - 1)" />
              </div>
              <div
                v-if="hoveredMsg === index || feedbackGiven[index]"
                class="feedback-btns"
              >
                <button
                  class="feedback-btn"
                  :class="{ 'feedback-active': feedbackGiven[index] === 'positive' }"
                  title="Útil"
                  @click="submitFeedback(index, 'positive')"
                >
                  <feather-icon
                    icon="ThumbsUpIcon"
                    size="13"
                  />
                </button>
                <button
                  class="feedback-btn"
                  :class="{ 'feedback-active-neg': feedbackGiven[index] === 'negative' }"
                  title="No útil"
                  @click="submitFeedback(index, 'negative')"
                >
                  <feather-icon
                    icon="ThumbsDownIcon"
                    size="13"
                  />
                </button>
              </div>
            </div>
            <div
              v-else
              class="chat-bubble bubble-user"
            >
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-html="formatMessage(msg.content)" />
            </div>
          </div>

          <!-- Tools used indicator — shown after last assistant message -->
          <div
            v-if="toolsUsed.length > 0 && messages.length > 0 && messages[messages.length - 1].role === 'assistant'"
            class="tools-used-indicator"
          >
            <feather-icon
              icon="SearchIcon"
              size="11"
            />
            <span>Consultó: {{ toolsUsedLabels.join(', ') }}</span>
            <span
              v-if="toolErrors.length > 0"
              class="tools-error-badge"
              :title="`Sin resultados en: ${toolErrors.map(t => toolLabels[t] || t).join(', ')}`"
            >
              <feather-icon
                icon="AlertTriangleIcon"
                size="11"
              />
              {{ toolErrors.length }} sin resultado
            </span>
          </div>

          <!-- Stream interrupted banner -->
          <div
            v-if="streamInterrupted && !loading"
            class="stream-interrupted-banner"
          >
            <feather-icon
              icon="AlertCircleIcon"
              size="14"
              class="mr-50"
            />
            La respuesta fue interrumpida.
            <button
              class="retry-btn"
              @click="retryLastMessage"
            >
              Reintentar
            </button>
          </div>

          <!-- Thinking indicator -->
          <div
            v-if="loading && !streaming"
            class="chat-message chat-message-assistant"
          >
            <div class="msg-avatar avatar-ai">
              <feather-icon
                icon="ZapIcon"
                size="16"
              />
            </div>
            <div class="chat-bubble bubble-assistant">
              <div class="typing-indicator">
                <span /><span /><span />
              </div>
              <div class="loading-message-text">
                {{ loadingMessage }}
              </div>
              <div
                v-if="activeTools.length > 0"
                class="active-tools-list"
              >
                <span
                  v-for="tool in activeTools"
                  :key="tool"
                  class="active-tool-badge"
                >
                  <span class="tool-spinner" />
                  {{ toolLabels[tool] || tool }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Input area -->
        <div class="chat-input-area">
          <div
            v-if="!chatEnabled"
            class="chat-disabled-notice"
          >
            <feather-icon
              icon="LockIcon"
              size="14"
              class="mr-50"
            />
            El asistente IA no está habilitado para tu cuenta. Contacta al administrador.
          </div>
          <template v-else>
            <div
              class="input-wrapper"
              :class="{ 'input-focused': inputFocused }"
            >
              <input
                ref="chatInput"
                v-model="userMessage"
                type="text"
                class="chat-input"
                placeholder="Escribe tu pregunta aqui..."
                :disabled="loading"
                @keydown.enter.prevent="sendMessage"
                @focus="inputFocused = true"
                @blur="inputFocused = false"
              >
              <button
                class="send-btn"
                :class="{ 'send-btn-active': userMessage.trim() && !loading }"
                :disabled="loading || !userMessage.trim()"
                @click="sendMessage"
              >
                <feather-icon
                  icon="ArrowUpIcon"
                  size="16"
                />
              </button>
            </div>
            <div
              v-if="userMessage.length > 0"
              class="char-counter"
              :class="{
                'char-counter-warning': userMessage.length > 1800,
                'char-counter-danger': userMessage.length >= 2000,
              }"
            >
              {{ userMessage.length }} / 2000
            </div>
          </template>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import { jsPDF } from 'jspdf'
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import { mixinChat } from '@/utils/mixins/chat'

export default {
  name: 'ChatAssistant',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BDropdown,
    BDropdownItem,
  },
  mixins: [mixinChat],
  data() {
    return {
      sidebarCollapsed: false,
      hoveredMsg: null,
      showSearch: false,
      searchQuery: '',
      frequentQuestions: [
        'Cuantos pedidos pendientes tengo?',
        'Mostrame mis ultimas 5 cotizaciones',
        'Buscar producto por SKU: ',
        'Verificar stock del producto: ',
        'Buscar cliente por nombre: ',
        'Detalle del pedido #',
        'Cuales pedidos estan en despacho?',
        'Mostrame cotizaciones aprobadas',
      ],
    }
  },

  computed: {
    searchMatchCount() {
      if (!this.searchQuery) return 0
      const q = this.searchQuery.toLowerCase()
      return this.messages.filter(m => {
        const text = (m.content || '').replace(/<[^>]+>/g, '').toLowerCase()
        return text.includes(q)
      }).length
    },
  },

  methods: {
    toggleSearch() {
      this.showSearch = !this.showSearch
      if (this.showSearch) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus()
        })
      } else {
        this.searchQuery = ''
      }
    },

    clearSearch() {
      this.searchQuery = ''
      this.showSearch = false
    },

    messageMatchesSearch(msg) {
      if (!this.searchQuery) return true
      const q = this.searchQuery.toLowerCase()
      const text = (msg.content || '').replace(/<[^>]+>/g, '').toLowerCase()
      return text.includes(q)
    },

    exportAsText() {
      const lines = this.messages.map(m => {
        const role = m.role === 'user' ? 'Tú' : 'Asistente Dutton'
        const content = m.content.replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
        return `[${role}]\n${content}\n`
      })
      const text = lines.join('\n---\n\n')
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `chat-dutton-${new Date().toISOString().slice(0, 10)}.txt`
      a.click()
      URL.revokeObjectURL(url)
    },

    exportAsPdf() {
      // eslint-disable-next-line new-cap
      const doc = new jsPDF()
      const pageWidth = doc.internal.pageSize.getWidth()
      const margin = 15
      const maxWidth = pageWidth - margin * 2
      let y = 20

      doc.setFontSize(16)
      doc.setTextColor(18, 64, 111)
      doc.text('Conversación - Asistente Dutton', margin, y)
      y += 8

      doc.setFontSize(9)
      doc.setTextColor(150, 150, 150)
      doc.text(new Date().toLocaleDateString('es-GT', { dateStyle: 'long' }), margin, y)
      y += 10

      this.messages.forEach(m => {
        const role = m.role === 'user' ? 'Tú' : 'Asistente Dutton'
        const content = m.content.replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')

        // Role label
        doc.setFontSize(9)
        doc.setTextColor(m.role === 'user' ? 18 : 100, m.role === 'user' ? 64 : 100, m.role === 'user' ? 111 : 100)
        doc.setFont(undefined, 'bold')
        doc.text(role, margin, y)
        y += 5

        // Content
        doc.setFont(undefined, 'normal')
        doc.setFontSize(9)
        doc.setTextColor(50, 50, 50)
        const lines = doc.splitTextToSize(content, maxWidth)
        lines.forEach(line => {
          if (y > 270) { doc.addPage(); y = 20 }
          doc.text(line, margin, y)
          y += 5
        })
        y += 4

        // Separator
        doc.setDrawColor(230, 230, 230)
        doc.line(margin, y, pageWidth - margin, y)
        y += 6
      })

      doc.save(`chat-dutton-${new Date().toISOString().slice(0, 10)}.pdf`)
    },
  },
}
</script>

<style scoped>
.chat-page {
  display: flex;
  height: calc(100vh - 8.5rem);
  gap: 0.5rem;
}

/* ---- Sidebar ---- */
.sidebar-panel {
  position: relative;
  width: 280px;
  min-width: 280px;
  height: 100%;
  transition: width 0.3s ease, min-width 0.3s ease;
}

.sidebar-panel.sidebar-collapsed {
  width: 36px;
  min-width: 36px;
}

.sidebar-toggle-btn {
  position: absolute;
  top: 0.5rem;
  right: -14px;
  z-index: 5;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #ebe9f1;
  background: #fff;
  color: #5e5873;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
}

.sidebar-toggle-btn:hover {
  background: #12406f;
  color: #fff;
  border-color: #12406f;
}

.sidebar-collapsed .sidebar-toggle-btn {
  right: -14px;
}

.sidebar-card {
  height: 100%;
  overflow-y: auto;
}

/* ---- Chat panel ---- */
.chat-panel {
  flex: 1;
  min-width: 0;
  height: 100%;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-list li {
  display: flex;
  align-items: center;
  padding: 0.35rem 0;
  font-size: 0.9rem;
  color: #5e5873;
}

.faq-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.faq-btn {
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: 0.5rem 0.65rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  background: #fff;
  color: #5e5873;
  font-size: 0.85rem;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
}

.faq-btn:hover:not(:disabled) {
  background: #f3f2f7;
  border-color: #12406f;
  color: #12406f;
}

.faq-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ---- Chat card ---- */
.chat-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chat-header {
  flex-shrink: 0;
  border-bottom: 1px solid #ebe9f1;
  padding: 0.75rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.avatar-icon {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #12406f 0%, #1a5a9e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

/* ---- Messages area ---- */
.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem;
  min-height: 0;
}

.welcome-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  padding: 2rem;
}

.welcome-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #12406f 0%, #1a5a9e 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  margin-bottom: 1rem;
}

.welcome-stats {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
}

.chat-message {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 0.5rem;
}

.chat-message-user {
  flex-direction: row-reverse;
}

.msg-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-user {
  background: #12406f;
  color: #fff;
}

.avatar-ai {
  background: #dce8f5;
  color: #12406f;
}

.message-bubble-wrapper {
  display: flex;
  flex-direction: column;
  max-width: 70%;
}

.chat-bubble {
  max-width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 1rem;
  word-wrap: break-word;
  overflow-x: auto;
  line-height: 1.55;
  font-size: 0.95rem;
}

.chat-message-user .chat-bubble {
  max-width: 70%;
}

.bubble-user {
  background-color: #12406f;
  color: #fff;
  border-top-right-radius: 0.25rem;
}

.bubble-user ::v-deep p:last-child,
.bubble-assistant ::v-deep p:last-child {
  margin-bottom: 0;
}

.chat-bubble ::v-deep table {
  border-collapse: collapse;
  font-size: 0.85rem;
  margin: 0.5rem 0;
  border: 1px solid #12406f;
  border-radius: 4px;
}

.chat-bubble ::v-deep th,
.chat-bubble ::v-deep td {
  border: 1px solid rgba(18, 64, 111, 0.3);
  padding: 0.35rem 0.6rem;
  white-space: nowrap;
}

.chat-bubble ::v-deep th {
  background: #dce8f5;
  font-weight: 600;
  color: #12406f;
}

.chat-bubble ::v-deep td {
  background: #fff;
}

.chat-bubble ::v-deep tr:hover td {
  background: #f0f5fc;
}

.chat-bubble ::v-deep .chat-entity-link {
  color: #12406f;
  font-weight: 600;
  text-decoration: underline;
  text-decoration-style: dotted;
  cursor: pointer;
}

.chat-bubble ::v-deep .chat-entity-link:hover {
  text-decoration-style: solid;
}

.bubble-assistant {
  background-color: #f3f2f7;
  color: #333;
  border-top-left-radius: 0.25rem;
}

/* ---- Copy button ---- */
.bubble-assistant {
  position: relative;
}

.copy-btn {
  position: absolute;
  top: 6px;
  right: 8px;
  background: rgba(255, 255, 255, 0.8);
  border: none;
  cursor: pointer;
  color: #b4b2be;
  padding: 2px 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s, background 0.2s;
  line-height: 1;
}

.copy-btn:hover {
  color: #12406f;
  background: #fff;
}

/* ---- Feedback buttons ---- */
.feedback-btns {
  display: flex;
  gap: 2px;
  margin-top: 3px;
}

.feedback-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #b4b2be;
  padding: 2px 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  transition: color 0.2s;
}

.feedback-btn:hover {
  color: #12406f;
}

.feedback-active {
  color: #28c76f !important;
}

.feedback-active-neg {
  color: #ea5455 !important;
}

/* ---- Tools used indicator ---- */
.tools-used-indicator {
  font-size: 11px;
  color: #b4b2be;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: -0.5rem;
  margin-bottom: 0.75rem;
  padding-left: 40px;
}

/* ---- Typing indicator ---- */
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0.25rem 0;
}

.typing-indicator span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #a8a5b5;
  animation: typing 1.2s infinite ease-in-out;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
  30% { transform: translateY(-4px); opacity: 1; }
}

.loading-message-text {
  font-size: 0.82rem;
  color: #a8a5b5;
  margin-top: 0.35rem;
}

/* ---- Input area ---- */
.chat-input-area {
  flex-shrink: 0;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #ebe9f1;
}

.input-wrapper {
  display: flex;
  align-items: center;
  background: #f8f8f8;
  border: 2px solid #ebe9f1;
  border-radius: 1.5rem;
  padding: 0.25rem 0.25rem 0.25rem 1rem;
  transition: all 0.2s ease;
}

.input-wrapper.input-focused {
  border-color: #12406f;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(18, 64, 111, 0.08);
}

.chat-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.95rem;
  color: #5e5873;
  padding: 0.45rem 0;
  min-width: 0;
}

.chat-input::placeholder {
  color: #b4b2be;
}

.chat-input:disabled {
  opacity: 0.6;
}

.send-btn {
  width: 34px;
  height: 34px;
  min-width: 34px;
  padding: 0;
  border-radius: 50%;
  border: none;
  background: #d8d6de;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.2s ease;
}

.send-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.send-btn-active {
  background: #12406f;
  color: #fff;
}

.send-btn-active:hover {
  background: #0d2d4f;
}

/* ---- Active tools list ---- */
.active-tools-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 0.4rem;
}

.active-tool-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: #12406f;
  background: #dce8f5;
  padding: 2px 8px;
  border-radius: 10px;
}

.tool-spinner {
  display: inline-block;
  width: 8px;
  height: 8px;
  border: 1.5px solid #12406f;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ---- Mobile responsive ---- */
@media (max-width: 767.98px) {
  .chat-page {
    flex-direction: column;
    height: calc(100dvh - 8.5rem);
  }

  .sidebar-panel {
    display: none;
  }

  .chat-card {
    height: 100%;
    position: relative;
  }

  .chat-body {
    padding-bottom: 0.5rem;
  }

  .chat-input-area {
    position: sticky;
    bottom: 0;
    z-index: 10;
    background: #fff;
    border-top: 1px solid #ebe9f1;
  }
}

/* ---- Stream interrupted banner ---- */
.stream-interrupted-banner {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.82rem;
  color: #ff9f43;
  margin-bottom: 0.75rem;
  padding-left: 40px;
}

.retry-btn {
  background: none;
  border: 1px solid #ff9f43;
  color: #ff9f43;
  padding: 1px 8px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.78rem;
  transition: all 0.15s;
}

.retry-btn:hover {
  background: #ff9f43;
  color: #fff;
}

/* ---- Char counter ---- */
.char-counter {
  font-size: 0.75rem;
  color: #b4b2be;
  text-align: right;
  margin-top: 3px;
  padding-right: 4px;
}

.char-counter-warning {
  color: #ff9f43;
}

.char-counter-danger {
  color: #ea5455;
  font-weight: 600;
}

/* ---- Search bar ---- */
.search-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  border-bottom: 1px solid #ebe9f1;
  background: #fafafa;
  flex-shrink: 0;
}

.search-icon {
  color: #b4b2be;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  color: #5e5873;
}

.search-results-count {
  font-size: 0.75rem;
  color: #b4b2be;
  flex-shrink: 0;
}

.search-clear-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #b4b2be;
  padding: 2px;
  display: flex;
  align-items: center;
  transition: color 0.15s;
}

.search-clear-btn:hover {
  color: #5e5873;
}

.btn-search-active {
  color: #12406f !important;
  background: #dce8f5 !important;
}

/* ---- Dimmed messages during search ---- */
.msg-dimmed {
  opacity: 0.25;
}

/* ---- Tools error badge ---- */
.tools-error-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: #ff9f43;
  font-size: 11px;
  margin-left: 6px;
}

/* ---- Chat disabled ---- */
.chat-disabled-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  color: #b4b2be;
  padding: 0.75rem 0;
}
</style>
